import { useState, useEffect, memo, useCallback } from 'react'
import { Alert, Button, FormControl, InputLabel, MenuItem, Paper, Select, Stack, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';

import config 			from './common/Config';
import useFetch 		from './api/api'

import CurrencyField 	from './components/CurrencyField';
import Loading 			from './components/Loading';
import NumericField 	from './components/NumericField';

function BankSavings() {
	const [inputFields, setInputFields] 	= useState([
		{id: 0, name: '', type: '', dateInit: 0, yield: 0, balance: 0, currency: ''}
	])

	const [saveSuccessfully, setSaveSuccessfully] 		= useState(false)
	const [errorSaving, setErrorSaving] 				= useState(false)
	const [deleteSuccessfully, setDeleteSuccessfully] 	= useState(false)
	const [isLoading, setIsLoading]						= useState(true)		
	
	const privateFetch = useFetch()

	useEffect(() => {
		privateFetch(config.apiUrl +'/api/bankSavings')
		.then((data) => data.json())
		.then((data) => {
			setInputFields(data)
			setIsLoading(false)
		})
	}, [])

	const handleAddFields = () => {
		const id = "create_"+ Date.now() + Math.random();
		let newfield = {id, name: '', type: '', dateInit: 0, yield: 0, balance: 0, currency: ''}

		setInputFields([...inputFields, newfield])
	}

	const handleRemoveFields = (index) => {
		let data = [...inputFields];
		
		let options = {
			method : 'DELETE',
			headers : {
				'content-type' : 'application/json; charset=utf-8',
			}
		}	
		privateFetch(config.apiUrl +'/api/bankSavings/'+ index, options)
		.then((data) => {
			if (data.status === 200) {
				setDeleteSuccessfully(true)
			}
			else {
				setErrorSaving(true)
			}
		})

		data.splice(data.findIndex(d => d.id === index), 1)
		setInputFields(data)

	}

	const handleFormChange = useCallback((id, event) => {
		const { name, value } = event.target ?? {};
		const nextValue = name ? value : event?.toISOString()?.substring(0, 10);

		setInputFields(prev =>
			prev.map(item =>
			item.id === id ? { ...item, [name || 'dateInit']: nextValue } : item
			)
		);
	}, []);

	const handleSaveTransactions = () => {

		const payload = inputFields.map(({ id, ...rest }) =>
		typeof id === 'string' && id.startsWith('create_')
			? { ...rest, frontEndId : id }	// cria novo: sem id
			: { ...rest, id } 				// atualiza existente
		);

		let bodyJson = `{
			"accounts"	: `+ JSON.stringify(payload) +`
		}`

		let options = {
			method : 'POST',
			headers : {
				'content-type' : 'application/json; charset=utf-8',
			},
			body : bodyJson
		}

		privateFetch(config.apiUrl +"/api/bankSavings", options)
		.then((data) => {
			if (data.status === 200) {
				setSaveSuccessfully(true)
			}
			else {
				setErrorSaving(true)
			}
		})
	}

	if (isLoading) return <Loading/>

	return (
		<Paper sx={{marginLeft: 2, marginRight : 4, marginBottom: 2}} elevation={2}>
			{ saveSuccessfully ? <Alert severity="success" onClose={() => {setSaveSuccessfully(false)}}>'Saldos atualizados com sucesso!'</Alert> : null }
			{ deleteSuccessfully ? <Alert severity="success" onClose={() => {setDeleteSuccessfully(false)}}>'Conta removida com sucesso!'</Alert> : null }
			{ errorSaving ? <Alert severity="error" onClose={() => {setErrorSaving(false)}}>'Erro ao salvar saldos!'</Alert> : null }

			{inputFields.map((input, index)=> (
				<BankSavingRow key={index} index={index} inputFields={input} onChange={handleFormChange} onDelete={handleRemoveFields}/>
			))}

			<Stack spacing={2} padding={2} direction={"row"} alignItems={"center"}>
				<Button variant="contained" onClick={handleAddFields}>Adicionar +</Button>
				<Button variant="contained" onClick={handleSaveTransactions}>Salvar</Button>
			</Stack>

		</Paper>
	)
}

const BankSavingRow = memo(({index, inputFields, onChange, onDelete}) => {
	
	const handleFormChange = (index, event) => {
		if (onChange) onChange(index, event)
	}

	const handleRemoveFields = (index) => {
		if (onDelete) onDelete(index)
	}

	return (
		<Stack spacing={2} padding={2} direction={"row"} alignItems={"center"} key={index}>
			<TextField name="name" label="Nome da Conta" sx={{width:200}} value={inputFields.name} onChange={event => handleFormChange(inputFields.id, event)}/>
			<FormControl  sx={{minWidth: 200}}>
				<InputLabel id="select-type-label">Tipo</InputLabel>
				<Select
					labelId="select-type-label"
					id="select-type"
					name="type"
					value={(inputFields.type)}
					label="Tipo"
					onChange={event => handleFormChange(inputFields.id, event)}
					>
					<MenuItem value={'checkings'}>Conta Corrente</MenuItem>
					<MenuItem value={'savings'}>Poupança</MenuItem>
				</Select>
			</FormControl>
			<CurrencyField name="balance" label="Saldo" sx={{width:200}} value={inputFields.balance} adornment={inputFields.currency == 'USD' ? "U$" : "R$"} onChange={event => handleFormChange(inputFields.id, event)}/>
			<FormControl  sx={{minWidth: 200}}>
				<InputLabel id="select-currency-label">Moeda</InputLabel>
				<Select
					labelId="select-currency-label"
					id="select-currency"
					name="currency"
					value={inputFields.currency}
					label="Moeda"
					onChange={event => handleFormChange(inputFields.id, event)}
					>
					<MenuItem value={'BRL'}>Real</MenuItem>
					<MenuItem value={'USD'}>Dólar</MenuItem>
				</Select>
			</FormControl>
			<DatePicker 
				format="DD/MM/YYYY"
				name="dataInit" 
				label="Deposito Inicial" 
				value={moment(inputFields.dateInit)}
				sx={{width:200, display: (inputFields.type === 'savings' ? 'block' : 'none')}} 
				onChange={event => handleFormChange(inputFields.id, event)}/>
			<NumericField 
				name="yield" 
				label="Taxa" 
				sx={{width:200, display: (inputFields.type === 'savings' ? 'block' : 'none')}} 
				value={inputFields.yield} 
				onChange={event => handleFormChange(inputFields.id, event)}
			/>

			<Button variant="contained" onClick={() => handleRemoveFields(inputFields.id)}>Remover</Button>
		</Stack>
	)
})

export default BankSavings